import { useState, useEffect } from 'react';
import { useAssignKioskStore } from '../../stores/assign-kiosk-store';
import { LocationAccordionList } from '../molecules/LocationAccordionList';
import { LocationAccordionRow } from '../molecules/LocationAccordionRow';
import { CommunitySearchBar } from '@/features/communities/components/molecules/CommunitySearchBar';
import { AlertCircle } from 'lucide-react';

const WarningIcon = () => (
  <svg className="h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
  </svg>
);

export function EvKioskScreen() {
  const {
    kioskDetails,
    screenType,
    locations,
    selectedLocationId,
    locationSearchQuery,
    assignError,
    assignSuccess,
    assignSuccessMessage,
    isLoadingLocations,
    locationsFetched,
    connectorLimitError,
    isAssigning,
    locationsSnapshot,
    selectedLocationIdSnapshot,
    setLocationSearch,
    setExpandedLocation,
    setSelectedLocationId,
    clearAllConnectors,
    fetchAllLocations,
    assignLocation,
  } = useAssignKioskStore();

  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showFullList, setShowFullList] = useState(false);
  const [savedLocationId, setSavedLocationId] = useState<string | null>(null);

  useEffect(() => {
    if (assignSuccess) {
      setShowFullList(false);
      setSavedLocationId(null);
    }
  }, [assignSuccess]);

  const isSparkType = screenType === 'SPARK_DEVICE_START' || screenType === 'SPARK_REMOTE_START';
  const isAssigned = isSparkType ? !!selectedLocationId : !!kioskDetails?.locationId;
  const assignedLocationId = isSparkType ? selectedLocationId : kioskDetails?.locationId ?? null;
  const headerLocationId = showFullList ? savedLocationId : assignedLocationId;
  const headerLocation = headerLocationId ? locations.find((l) => l.id === headerLocationId) : null;
  const showAssignedHeader = showFullList ? !!savedLocationId : isAssigned;
  const displayFullList = !isAssigned || showFullList;
  const isSaveMode = isAssigned && !showFullList;

  const liveSelectedLocation = selectedLocationId ? locations.find((l) => l.id === selectedLocationId) : null;
  const selectedConnectorCount =
    liveSelectedLocation?.evses.flatMap((e) => e.connectors).filter((c) => c.isSelected).length ?? 0;

  // ── Button / validation state ──────────────────────────────────────────────
  const hasUnsavedChanges = (() => {
    if (selectedLocationId !== selectedLocationIdSnapshot) return true;
    if (!selectedLocationId) return false;
    const current = locations.find((l) => l.id === selectedLocationId);
    const snapshot = locationsSnapshot.find((l) => l.id === selectedLocationId);
    if (!current || !snapshot) return true;
    const toKey = (loc: typeof current) =>
      loc.evses.flatMap((e) => e.connectors.map((c) => `${c.id}:${c.isSelected}:${c.alias}:${c.displayOrder}`)).join('|');
    return toKey(current) !== toKey(snapshot);
  })();

  const { hasDuplicateLabels, hasMissingLabels } = (() => {
    if (!liveSelectedLocation || screenType !== 'SPARK_DEVICE_START') return { hasDuplicateLabels: false, hasMissingLabels: false };
    const selected = liveSelectedLocation.evses
      .flatMap((e) => e.connectors.map((c) => ({ alias: c.alias?.trim() ?? '', isSelected: c.isSelected })))
      .filter((c) => c.isSelected);
    const nonEmpty = selected.filter((c) => c.alias !== '').map((c) => c.alias);
    return {
      hasDuplicateLabels: nonEmpty.length !== new Set(nonEmpty).size,
      hasMissingLabels: selected.some((c) => c.alias !== '') && selected.some((c) => c.alias === ''),
    };
  })();
  const hasNicknameError = hasDuplicateLabels || hasMissingLabels;

  const sparkHasNoConnectors = isSparkType && !!selectedLocationId && selectedConnectorCount === 0 && !isLoadingLocations;
  const buttonDisabled = isAssigning || hasNicknameError || !selectedLocationId || sparkHasNoConnectors || (isSaveMode && !hasUnsavedChanges);

  // ── Footer message (priority order) ───────────────────────────────────────
  type FooterVariant = 'error' | 'warning' | 'success' | 'none';
  const footerVariant: FooterVariant =
    assignError ? 'error' :
    hasDuplicateLabels ? 'warning' :
    hasMissingLabels ? 'warning' :
    connectorLimitError ? 'warning' :
    assignSuccess ? 'success' :
    'none';

  const footerMessage: string | null =
    assignError ? assignError :
    hasDuplicateLabels ? 'Duplicate Labels detected. Each label must be unique.' :
    hasMissingLabels ? 'If any EVSE has a Label, then all selected EVSEs must have one.' :
    connectorLimitError ? connectorLimitError :
    assignSuccess ? assignSuccessMessage :
    null;


  return (
    <div className="flex flex-col gap-3 pb-16">

      {/* ── Assigned location header ───────────────────────────────────────── */}
      {showAssignedHeader && (
        <div className={`flex flex-col gap-3${showFullList ? ' sticky top-0 z-10 bg-gray-100 pb-2' : ''}`}>
          <div className="flex items-center gap-2 text-sm font-medium text-blue-800 min-w-0">
            <svg className="h-4 w-4 shrink-0 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span className="truncate">Currently assigned: {headerLocation?.name ?? kioskDetails?.locationName}</span>
            {headerLocationId && (
              <span className="shrink-0 font-normal text-blue-600">({headerLocationId})</span>
            )}
          </div>

          {headerLocation ? (
            <LocationAccordionRow
              location={headerLocation}
              hideSwitch
              outlined
              headerAction={selectedConnectorCount > 0 ? (
                <button
                  onClick={() => setShowClearDialog(true)}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Clear All
                </button>
              ) : undefined}
            />
          ) : isLoadingLocations ? (
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-4">
              <div className="h-4 w-44 animate-pulse rounded bg-gray-200" />
            </div>
          ) : (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              Location ID: {headerLocationId}
            </div>
          )}

          {!showFullList && (
            <div className="flex justify-start">
              <button
                onClick={() => {
                  setSavedLocationId(assignedLocationId);
                  setShowFullList(true);
                  setExpandedLocation(null);
                  fetchAllLocations();
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Change Location
              </button>
            </div>
          )}

          {showFullList && locations.length > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <CommunitySearchBar
                  value={locationSearchQuery}
                  onChange={setLocationSearch}
                  placeholder="Search locations…"
                  className="w-full sm:w-72"
                />
              </div>
              <button
                onClick={() => {
                  setSelectedLocationId(savedLocationId);
                  setSavedLocationId(null);
                  setShowFullList(false);
                }}
                className="shrink-0 rounded-lg border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Full location list ─────────────────────────────────────────────── */}
      {displayFullList && (
        <>
          {!showAssignedHeader && (
            <div className="flex items-center gap-3 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm font-medium text-yellow-800">
              <svg className="h-5 w-5 shrink-0 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              The device is not mapped to a location - please map it to the relevant location.
            </div>
          )}

          {!showAssignedHeader && locations.length > 0 && (
            <div className="sticky top-0 z-10 bg-gray-50 py-2">
              <CommunitySearchBar
                value={locationSearchQuery}
                onChange={setLocationSearch}
                placeholder="Search locations…"
                className="w-full sm:w-72"
              />
            </div>
          )}

          {!isLoadingLocations && locationsFetched && locations.length === 0 && (
            <div className="flex min-h-[32rem] items-center justify-center rounded-lg border border-gray-200 bg-white">
              <p className="text-sm text-gray-400">No locations found for this operator.</p>
            </div>
          )}

          <LocationAccordionList excludeLocationId={savedLocationId} />
        </>
      )}

      {/* ── Floating bottom: message box left + button right ──────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex items-end justify-between px-4 py-3 pointer-events-none">
        <div className="pointer-events-auto">
          {footerMessage && (
            <div className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium shadow-md opacity-90 ${footerVariant === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
                footerVariant === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
                'bg-yellow-50 border border-yellow-200 text-yellow-800'}`}
            >
              {footerVariant === 'error' && <AlertCircle className="h-4 w-4 shrink-0" />}
              {footerVariant === 'warning' && <WarningIcon />}
              <span>{footerMessage}</span>
            </div>
          )}
        </div>
        <button
          disabled={buttonDisabled}
          onClick={assignLocation}
          className="pointer-events-auto ml-4 shrink-0 flex items-center gap-2 rounded-lg bg-yellow-400 px-6 py-2 text-sm font-bold text-gray-900 shadow hover:bg-yellow-500 disabled:opacity-50 opacity-90"
        >
          {isAssigning && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-700 border-t-transparent" />
          )}
          {isSaveMode ? 'Save' : 'Assign'}
        </button>
      </div>

      {/* ── Clear All confirmation dialog ──────────────────────────────────── */}
      {showClearDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="w-96 rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="mb-3 text-xl font-bold text-gray-900">Clear All Selections</h2>
            <p className="mb-6 text-sm text-gray-600">
              Are you sure you want to clear all {selectedConnectorCount} selected connector{selectedConnectorCount !== 1 ? '(s)' : ''}? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-6">
              <button
                onClick={() => setShowClearDialog(false)}
                className="text-sm font-semibold text-yellow-500 hover:text-yellow-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (headerLocationId) clearAllConnectors(headerLocationId);
                  setShowClearDialog(false);
                }}
                className="text-sm font-semibold text-gray-800 hover:text-gray-900"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
